import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';

/**
 * Closing Lifestyle CTA — mirrors the final "Make Your Move to FLATZ 520 Today!"
 * band on flatz520.com/p/lifestyle/. A full-bleed lifestyle image with a short
 * two-line headline and a single primary CTA (Schedule a Tour).
 */

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 8,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A warm, aspirational closing image for the Lifestyle page — a pool at golden hour, rooftop lounge, signature courtyard, or lifestyle moment that leaves a strong final impression.',
    context:
      'Full-bleed background for the closing CTA band on the Lifestyle page. Should feel like the emotional payoff — not a utilitarian unit shot.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function LifestyleCTA() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();
  const color4 = useColor4();

  const bg = useSlotGroup('lifestylecta.bg_image', bgImageDef);
  const cta = useSlotGroup('lifestylecta.cta', ctaDef);

  return (
    <section className="relative w-full overflow-hidden">
      <EditableSlotGroup groupPath="lifestylecta.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt={bg.alt || ''}
              sizes="100vw"
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-primary)]" />
          )}
        </div>
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/45" />

      <div
        ref={sectionRef}
        className="reveal-fade-up relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center text-white"
      >
        <RichTextContent
          path="lifestylecta.title"
          label="Title"
          defaultValue='<p>Make Your Move to<br><span data-type="field-token" data-field="property_name"></span> Today!</p>'
          as="h2"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal leading-tight tracking-tight max-w-3xl mx-auto drop-shadow"
        />

        <div className="mt-10 flex items-center justify-center">
          <EditableSlotGroup groupPath="lifestylecta.cta" definition={ctaDef}>
            <a
              href={cta.url || '/schedule-a-tour'}
              target={cta._target || '_self'}
              style={color4?.primaryOverride}
              className="lifestyle-cta-btn group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta.label || 'Schedule A Tour'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .lifestyle-cta-btn {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .lifestyle-cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .lifestyle-cta-btn:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .lifestyle-cta-btn:hover::before {
          left: 100%;
        }
        .lifestyle-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleCTA',
  displayName: 'Lifestyle CTA',
  tokens: {
    'lifestylecta.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Make Your Move to<br><span data-type="field-token" data-field="property_name"></span> Today!</p>',
    },
  },
  slotGroups: {
    'lifestylecta.bg_image': bgImageDef,
    'lifestylecta.cta': ctaDef,
  },
};

(LifestyleCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LifestyleCTA;
